import { OutreachPresenceNote } from './OutreachPresenceNote';
import * as s from './OutreachStepCrumbs.css';

export type OutreachStep = 'candidates' | 'composer' | 'confirm';

export interface OutreachStepCrumbsProps {
  step: OutreachStep;
  /** 발송 확인에서만 준다 — 앞 단계에서는 아직 몇 회차가 될지 모른다. */
  round?: string;
  /** 발송 나라 — 대상 수와 한 덩어리로 읽힌다. */
  country?: string;
  /**
   * 이번에 메일을 받을 **계정** 수. 아직 못 세었으면 `null` — 「0명」으로 단정하지 않는다.
   *
   * 퀘스트 `da3f95d4` 본문이 「대상 수를 0명으로 고정해 보인다 — 버그」로 지목한 자리다. 되돌아가
   * 고치는 길은 이 줄의 「후보 선택」 이 이미 들고 있어 따로 버튼을 두지 않는다.
   */
  targetCount?: number | null;
  /**
   * 지난 단계로 되돌아간다. 없으면 이 줄은 읽기만 한다.
   *
   * **되돌아가는 유일한 길이다**(2026-09-28). 작성기·발송 확인의 머리줄을 지우면서 「닫기」가
   * 함께 사라졌다.
   */
  onStep?: (step: OutreachStep) => void;
  /** 주면 줄 오른쪽에 이 캠페인의 작업 중 표시를 둔다(IA §3-7). */
  scenarioId?: string;
}

const HEADING_ID = 'outreach-step-crumbs-heading';

const numberFormat = new Intl.NumberFormat('ko-KR');

const STEPS: { id: OutreachStep; label: string }[] = [
  { id: 'candidates', label: '후보 선택' },
  { id: 'composer', label: '메일 작성' },
  { id: 'confirm', label: '발송 확인' },
];

/**
 * 섭외 단계 줄 — 후보 선택 › 메일 작성 › 발송 확인.
 *
 * **어느 단계에 있는지 보여주는 것이 이 줄의 일이다**(2026-09-28). 예전에는 발송 확인 패널과
 * 작성기 머리줄에 따로 있어서 같은 줄이 두 번 서고, 후보 선택 단계에서는 아예 안 보였다.
 * 탭 바로 아래 한 자리에 서고, 그 아래에 그 단계의 내용이 온다.
 *
 * **앞으로는 못 뛴다.** 지난 단계만 누를 수 있다 — 후보를 안 고른 채 발송 확인으로 가면 빈
 * 명단을 보게 되고, 그 단계의 버튼이 순서를 지키는 유일한 장치다.
 */
export function OutreachStepCrumbs({
  step,
  round,
  country,
  targetCount,
  onStep,
  scenarioId,
}: OutreachStepCrumbsProps) {
  const currentIndex = STEPS.findIndex(({ id }) => id === step);

  /*
    `nav` 다 — 지난 단계를 눌러 되돌아가는 길이고, 읽어 주는 화면이 「건너뛰기」 목록에 올린다.
    이름은 `nav` 가 들고 `ol` 은 순서만 말한다(2026-09-29).
  */
  const list = (
    <nav className={s.nav} aria-labelledby={HEADING_ID}>
      {/*
        이름은 제목이 든다 — `aria-label` 만 두면 읽어 주는 화면의 제목 목록에 안 올라간다.
        화면에는 단계 이름들이 이미 보이므로 이 제목은 숨긴다.
      */}
      <h3 id={HEADING_ID} className={s.srOnly}>
        섭외 단계
      </h3>
      <ol className={s.crumbs}>
        {STEPS.map(({ id, label }, index) => {
          const isNow = id === step;
          const canGo = onStep !== undefined && index < currentIndex;
          return (
            <li key={id} aria-current={isNow ? 'step' : undefined}>
              {index > 0 && (
                <span aria-hidden="true" className={s.arrow}>
                  ›
                </span>
              )}
              {canGo ? (
                <button type="button" className={s.back} onClick={() => onStep(id)}>
                  {label}
                </button>
              ) : (
                <span className={isNow ? s.now : undefined}>{label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
  /*
    회차는 **줄 맨 오른쪽**이다(2026-09-29). 단계 목록 안에 두면 단계 뒤에 바로 붙어 네 번째
    단계처럼 읽히고, 작업 중 표시보다 앞서면 두 보조 문구가 가운데 몰린다.
  */
  const trailing = round === undefined ? null : <span className={s.round}>{round}</span>;
  /*
    「US · 1,020명 [대상 수정]」 — 나라만 알고 수를 아직 못 세었으면 나라만 적는다.

    「대상 수정」 은 왼쪽 「후보 선택」 crumb 과 **같은 곳으로 간다**. 같은 동작이 한 줄에 둘이지만
    승인 시안과 플랜이 둘 다 이 버튼을 둔다 — crumb 이 눌린다는 것은 밑줄로만 말해서, 대상을 고치러
    가는 길을 찾는 사람에게는 이 이름이 필요하다.
  */
  const target =
    country === undefined ? null : (
      <span className={s.target}>
        {country}
        {targetCount === undefined || targetCount === null
          ? ''
          : ` · ${numberFormat.format(targetCount)}명`}
        {onStep === undefined ? null : (
          <button type="button" className={s.editTargets} onClick={() => onStep('candidates')}>
            대상 수정
          </button>
        )}
      </span>
    );
  if (scenarioId === undefined && trailing === null && target === null) return list;
  return (
    <div className={s.row}>
      {list}
      {target}
      {scenarioId === undefined ? null : <OutreachPresenceNote scenarioId={scenarioId} />}
      {trailing}
    </div>
  );
}
